import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import Handler from "../../../Helpers/Handler";
import { formatDateTime, textCapitalize } from "../../../Helpers/Utils";
import { DetailCard, FieldRow, Skeleton, StatusPill } from "../../../Components/Needed";
import { formatPreviousValue, verificationStageLabel } from "../shared/vehicleFleetConstants";
import VehicleImagesPanel from "../shared/VehicleImagesPanel";
import VehicleDevicePanel from "../shared/VehicleDevicePanel";
import VehicleVerifyPanel from "../shared/VehicleVerifyPanel";
import VehiclePricingPanel from "../shared/VehiclePricingPanel";
import VehicleDocumentsPanel from "../shared/VehicleDocumentsPanel";
import BikeFormModal from "./BikeFormModal";
import VehicleServicePanel from "../shared/VehicleServicePanel";
import Timeline from "../../../Components/Timeline";
import { listBackPath } from "../../../Helpers/useListParams";

// Same shape as CarDetail.js, minus what Bike doesn't have: no
// bookingCounts (bikeDetail joins no booking collection), no insurance
// panel, and the device panel runs with hasFuel={false} since Bike carries
// no fuel field at all.
const BikeDetail = () => {
    const { bikeId } = useParams();
    const navigate = useNavigate();
    const [bike, setBike] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [togglingStatus, setTogglingStatus] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const [bikeResponse, logResponse] = await Promise.all([
            Handler({ method: "GET", url: `/staff/bike/${bikeId}` }),
            Handler({ method: "GET", url: `/staff/bike/${bikeId}/log`, params: { limit: 20 } }),
        ]);
        setBike(bikeResponse?.success ? bikeResponse?.data?.data : null);
        setLogs(logResponse?.success ? logResponse?.data?.data || [] : []);
        setLoading(false);
    }, [bikeId]);

    useEffect(() => { load(); }, [load]);

    const toggleStatus = async () => {
        setTogglingStatus(true);
        const response = await Handler({ method: "patch", url: `/staff/bike/${bikeId}/status`, data: { status: !bike?.status } });
        if (response?.success) {
            toast.success(response?.data?.message || "Status updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update status"));
        }
        setTogglingStatus(false);
    };

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="30%" className="mb-3" />
                <Skeleton height={120} />
            </div>
        );
    }

    if (!bike) {
        return (
            <div className="settings-card text-center py-5">
                <p className="text-muted mb-3">Bike not found.</p>
                <button type="button" className="btn back-btn" onClick={() => navigate(listBackPath("/vehicle-fleet/bikes"))}>Back to bikes</button>
            </div>
        );
    }

    return (
        <>
            <button type="button" className="btn back-btn text-decoration-none px-3 mb-3 d-inline-flex align-items-center gap-2" onClick={() => navigate(listBackPath("/vehicle-fleet/bikes"))}>
                <FaArrowLeft /> Back to bikes
            </button>

            <div className="row g-4">
                <div className="col-12 col-lg-7">
                    <DetailCard title={bike.registrationNo}>
                        <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                            <span className="badge rounded-pill text-bg-light border">{bike.brand?.brand}</span>
                            <span className="badge rounded-pill text-bg-light border">{bike.model?.modelName}</span>
                            <span className="badge rounded-pill text-bg-light border">{bike.variant?.variantName}</span>
                            <StatusPill active={bike.status} />
                        </div>
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <StatusPill active={bike.verificationStatus === "verified"} activeText="Verified" inactiveText={textCapitalize(bike.verificationStatus)} />
                            <span className="small text-muted">{verificationStageLabel(bike.verificationStage)} ({bike.verificationStage}%)</span>
                        </div>
                        <div className="progress mb-3" style={{ height: 6 }}>
                            <div className="progress-bar" role="progressbar" style={{ width: `${bike.verificationStage}%` }} />
                        </div>

                        <div className="d-flex gap-2 mb-3">
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={toggleStatus} disabled={togglingStatus}>
                                {togglingStatus ? "Updating..." : bike.status ? "Deactivate" : "Activate"}
                            </button>
                            <button type="button" className="btn btn-sm btn-primary" onClick={() => setShowEditModal(true)}>Edit bike</button>
                        </div>

                        <FieldRow label="Owner" value={bike.ownerId} />
                        <FieldRow label="Color" value={bike.color} />
                        <FieldRow label="Hub" value={bike.hubDetail ? [bike.hubDetail.buildingNo, bike.hubDetail.area, bike.hubDetail.city].filter(Boolean).join(", ") : "-"} />
                        <FieldRow label="Odometer" value={bike.odometer?.current !== undefined ? `${bike.odometer.current} km` : "-"} />
                        {bike.status === false && bike.errorMsg ? <FieldRow label="Deactivation reason" value={bike.errorMsg} /> : null}
                        <FieldRow label="Created" value={formatDateTime(bike.createdAt)} />
                        <FieldRow label="Last updated" value={formatDateTime(bike.updatedAt)} />
                    </DetailCard>

                    <DetailCard title="Images">
                        <VehicleImagesPanel vehicleType="bike" vehicleId={bikeId} images={bike.bikeImages} onSaved={load} />
                    </DetailCard>

                    <DetailCard title="Device & Odometer">
                        <VehicleDevicePanel vehicleType="bike" vehicleId={bikeId} vehicle={bike} hasImages={!!bike.bikeImages?.length} hasFuel={false} onSaved={load} />
                    </DetailCard>

                    <DetailCard title="Verification">
                        <VehicleVerifyPanel vehicleType="bike" vehicleId={bikeId} vehicle={bike} onSaved={load} />
                    </DetailCard>

                    <DetailCard title="Pricing">
                        <VehiclePricingPanel vehicleType="bike" vehicleId={bikeId} priceTypes={bike.priceTypes} onSaved={load} />
                    </DetailCard>

                    <DetailCard title="Documents">
                        <VehicleDocumentsPanel vehicleType="bike" vehicleId={bikeId} documents={bike.documents} onSaved={load} />
                    </DetailCard>

                    <DetailCard title="Service History">
                        <VehicleServicePanel vehicleType="bikes" vehicleId={bikeId} />
                    </DetailCard>
                </div>

                <div className="col-12 col-lg-5">
                    {/* <DetailCard title="Activity Log">
                        {logs.length ? (
                            <ul className="hub-log-list">
                                {logs.map((log) => (
                                    <li key={log._id || `${log.createdAt}-${log.action}`} className="hub-log-item">
                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                            <span className={`badge rounded-pill ${log.action === "Write" ? "text-bg-primary" : "text-bg-secondary"}`}>{log.action}</span>
                                            <span className="text-muted small">{formatDateTime(log.createdAt)}</span>
                                        </div>
                                        <div className="small mt-1">{log?.previousData?.msg || "-"}</div>
                                        {log?.previousData?.data && Object.keys(log.previousData.data).length ? (
                                            <div className="hub-log-diff">
                                                {Object.entries(log.previousData.data).map(([field, value]) => (
                                                    <div key={field}>
                                                        <span className="text-muted">{field}:</span> was {formatPreviousValue(value)}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : ""}
                                        <div className="text-muted small mt-1">By {log.userId}</div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-muted mb-0">No activity recorded yet.</p>
                        )}
                    </DetailCard> */}
                    <Timeline userLog={logs} />
                </div>
            </div>

            {showEditModal ? (
                <BikeFormModal
                    mode="edit"
                    bike={bike}
                    onClose={() => setShowEditModal(false)}
                    onSaved={() => { setShowEditModal(false); load(); }}
                />
            ) : ""}
        </>
    );
};

export default BikeDetail;
